/**
 * Screenshot Capture Utility
 * Provides functions to capture screenshots from iframe previews
 * and save them as project thumbnails
 */

import config from '../config';
import { getBffHeaders } from './apiHeaders';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import { devLog } from './devLog';

/**
 * Capture a screenshot from an iframe element
 * @param {HTMLIFrameElement} iframeElement - The iframe to capture
 * @param {Object} options - Capture options
 * @param {number} options.width - Target width (default: 1200)
 * @param {number} options.height - Target height (default: 675)
 * @param {number} options.quality - JPEG quality 0-1 (default: 0.8)
 * @param {number} options.delay - Delay before capture in ms (default: 1000)
 * @returns {Promise<string>} - Data URL of the captured screenshot
 */
export async function captureIframeScreenshot(iframeElement, options = {}) {
  const {
    width = 1200,
    height = 675,
    quality = 0.8,
    delay = 1000,
  } = options;

  try {
    devLog('📸 Capturing screenshot from iframe...');

    // Wait a bit for the iframe to fully render
    await new Promise(resolve => setTimeout(resolve, delay));

    // Try to use html2canvas if available, otherwise use simpler method
    if (window.html2canvas) {
      // Method 1: Use html2canvas for better quality (if library is loaded)
      try {
        const iframeDocument = iframeElement.contentDocument || iframeElement.contentWindow.document;
        const canvas = await window.html2canvas(iframeDocument.body, {
          width: width,
          height: height,
          scale: 1,
          useCORS: true,
          allowTaint: true,
          logging: false,
        });
        
        return canvas.toDataURL('image/jpeg', quality);
      } catch (error) {
        console.warn('⚠️  html2canvas failed, falling back to simpler method:', error);
      }
    }

    // Method 2: Use canvas drawImage with iframe (simpler, less accurate)
    // This works for same-origin iframes
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    // Try to capture iframe content
    try {
      const iframeDocument = iframeElement.contentDocument || iframeElement.contentWindow.document;
      const svgString = new XMLSerializer().serializeToString(iframeDocument.documentElement);
      const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);
      
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = url;
      });

      ctx.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      
      return canvas.toDataURL('image/jpeg', quality);
    } catch (error) {
      console.warn('⚠️  Direct capture failed, using placeholder:', error);
      
      // Method 3: Generate a placeholder with project info
      return generatePlaceholderThumbnail(canvas, ctx, width, height);
    }
  } catch (error) {
    console.error('❌ Error capturing screenshot:', error);
    throw error;
  }
}

/**
 * Generate a placeholder thumbnail when screenshot capture fails
 * @param {HTMLCanvasElement} canvas - Canvas element
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 * @returns {string} - Data URL of the placeholder
 */
function generatePlaceholderThumbnail(canvas, ctx, width, height) {
  // Create a gradient background
  const gradient = ctx.createLinearGradient(0, 0, width, height);
  gradient.addColorStop(0, '#1a1a2e');
  gradient.addColorStop(1, '#16213e');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);

  // Add some decorative elements
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
  ctx.lineWidth = 2;
  for (let i = 0; i < 5; i++) {
    ctx.strokeRect(
      Math.random() * width,
      Math.random() * height,
      Math.random() * 200 + 100,
      Math.random() * 150 + 75
    );
  }

  // Add text
  ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
  ctx.font = 'bold 48px Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('🚀', width / 2, height / 2);

  return canvas.toDataURL('image/jpeg', 0.8);
}

/**
 * Auto-capture screenshot from preview and save as thumbnail
 * Uses server-side capture with Puppeteer for cross-origin support
 * @param {HTMLIFrameElement} iframeElement - The iframe element (used to get preview URL)
 * @param {string} projectId - Project ID
 * @param {string} tenantId - Tenant ID (optional)
 * @param {Object} options - Capture options
 * @returns {Promise<Object>} - Result with success status and thumbnail URL
 */
export async function autoCaptureThumbnail(iframeElement, projectId, tenantId, options = {}) {
  try {
    devLog(`📸 Auto-capturing thumbnail for project: ${projectId}`);

    // Get preview URL from iframe
    const previewUrl = iframeElement?.src;
    if (!previewUrl) {
      throw new Error('No preview URL found in iframe');
    }

    devLog(`🖼️  Capturing from URL: ${previewUrl}`);
    devLog(`💾 Using server-side capture with Puppeteer...`);

    // Use server-side capture endpoint
    const response = await getAuthorizedFetch()(`${config.bffUrl}/api/thumbnails/capture`, {
      method: 'POST',
      headers: {
        ...getBffHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        project_id: projectId,
        tenant_id: tenantId,
        preview_url: previewUrl,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to capture thumbnail: ${response.statusText}`);
    }

    const result = await response.json();
    devLog('✅ Thumbnail captured and saved successfully:', result);

    // `result.thumbnail_url` is the GET /api/thumbnails/:id JSON API route,
    // not raw image bytes — an <img src> pointed at it renders as a broken
    // image. Resolve the actual data: URL the same way the rest of the app
    // reads thumbnails, so listeners of the `thumbnail-updated` event get a
    // real, renderable image.
    const thumbnailUrl = await getProjectThumbnail(projectId, tenantId);

    return {
      success: true,
      thumbnailUrl,
      message: 'Thumbnail captured and saved successfully',
    };
  } catch (error) {
    // A thumbnail is a nicety: a failed capture is the caller's to report (the manual capture does), never a console
    // error on every preview load.
    devLog('📸 Thumbnail capture failed:', error?.message || error);
    return {
      success: false,
      error: error.message,
      message: 'Failed to capture thumbnail',
    };
  }
}

/**
 * Get thumbnail for a project
 * @param {string} projectId - Project ID
 * @param {string} tenantId - Tenant ID (optional)
 * @returns {Promise<string|null>} - Thumbnail data URL or null if not found
 */
export async function getProjectThumbnail(projectId, tenantId) {
  try {
    const params = new URLSearchParams();
    if (tenantId) {
      params.append('tenant_id', tenantId);
    }

    const response = await getAuthorizedFetch()(
      `${config.bffUrl}/api/thumbnails/${projectId}?${params.toString()}`,
      {
        method: 'GET',
        headers: getBffHeaders(),
      }
    );

    if (!response.ok) {
      if (response.status === 404) {
        // Thumbnail not found - this is normal for new projects
        return null;
      }
      if (response.status === 500 || response.status === 503) {
        // Backend error (MongoDB not configured/timeout) - fail silently
        console.debug(`Thumbnail service unavailable for project ${projectId}`);
        return null;
      }
      // Other errors - log as warning
      console.warn(`Failed to get thumbnail for ${projectId}: ${response.statusText}`);
      return null;
    }

    const result = await response.json();
    return result.thumbnail;
  } catch (error) {
    // Network or parsing errors - fail silently
    console.debug(`Thumbnail fetch failed for ${projectId}:`, error.message);
    return null;
  }
}

/**
 * Schedule auto-capture after preview loads
 * @param {HTMLIFrameElement} iframeElement - The iframe element
 * @param {string} projectId - Project ID
 * @param {string} tenantId - Tenant ID (optional)
 * @param {number} delay - Delay in ms after iframe loads (default: 3000)
 */
// When each preview (project + URL) was last auto-captured: the iframe loads again on every refresh, rebuild and tab
// switch, and one thumbnail per few minutes is plenty.
const lastAutoCapture = new Map();
export const AUTO_CAPTURE_MIN_INTERVAL_MS = 5 * 60_000;

/** For tests: forget the auto-capture history. */
export function resetAutoCaptureHistory() {
  lastAutoCapture.clear();
}

export function scheduleAutoCapture(iframeElement, projectId, tenantId, delay = 3000) {
  if (!iframeElement || !projectId) {
    devLog('⚠️  Cannot schedule auto-capture: missing iframe or project ID');
    return;
  }
  const key = `${projectId} ${iframeElement.src || ''}`;
  const now = Date.now();
  if (now - (lastAutoCapture.get(key) || 0) < AUTO_CAPTURE_MIN_INTERVAL_MS) {
    devLog(`⏭️  Thumbnail for ${projectId} captured recently, not again`);
    return;
  }
  lastAutoCapture.set(key, now);

  devLog(`⏰ Scheduling auto-capture for project ${projectId} in ${delay}ms`);

  // Wait for iframe to load
  const handleLoad = () => {
    devLog(`🎬 Iframe loaded, waiting ${delay}ms before capture...`);
    
    setTimeout(() => {
      autoCaptureThumbnail(iframeElement, projectId, tenantId, {
        delay: 1000, // Additional delay after scheduled delay
      });
    }, delay);
  };

  if (iframeElement.contentDocument?.readyState === 'complete') {
    // Already loaded
    handleLoad();
  } else {
    // Wait for load event
    iframeElement.addEventListener('load', handleLoad, { once: true });
  }
}

export default {
  captureIframeScreenshot,
  autoCaptureThumbnail,
  getProjectThumbnail,
  scheduleAutoCapture,
};
